import { useScreenSize } from '@/themes/media-query'
import { DataGrid } from 'devextreme-react'
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import {
  Button as GridButton,
  Column,
  ColumnChooser,
  DataGridRef,
  DataGridTypes,
  Editing,
  Export,
  FilterPanel,
  FilterRow,
  HeaderFilter,
  Item,
  Pager,
  Scrolling,
  Search,
  SearchPanel,
  Selection,
  Sorting,
  StateStoring,
  Toolbar,
} from 'devextreme-react/data-grid'
import dxDataGrid, { ColumnButtonClickEvent, ExportingEvent, RowDblClickEvent } from 'devextreme/ui/data_grid'
import ODataStore from 'devextreme/data/odata/store'
import notify from 'devextreme/ui/notify'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { Workbook } from 'exceljs'
import { exportDataGrid } from 'devextreme/excel_exporter'
import saveAs from 'file-saver'
import { ClickEvent } from 'devextreme/ui/button'
import { AzureUserInfo, getAzureUserInformation, useTokenRefresh } from '@/auth/azure/azureManager'
import { columnSourceFactory, isUserRoleAllowed } from '@/routes/utils'
import { gridTotalItemsLabel } from '@/constants'
import DateColumn from '@/components/date-column/DateColumn'
import { dump, email_aziendale, sede, ticket, tipologia_dump } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'
import { DumpsGridProps } from '@/routes/dumps/DumpsGrid.types'
import { useNavigate } from 'react-router-dom'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { EdmLiteral } from 'devextreme/common/data'
import { StatoTicket, TipologiaTicket } from '@/routes/tickets/TicketEditor.enums'
import { Roles } from '@/auth/azure/Roles'
import { downloadDump, onCellPrepared } from '@/routes/dumps/utils'
import { createTicketWithApi } from '@/routes/tickets/ticketWrite'

export type IDumpsGridRef = {
  ref: () => React.RefObject<DataGridRef>
}

// eslint-disable-next-line react/display-name
const DumpsGrid = forwardRef<IDumpsGridRef, { currentFilter?: any[]; onFocusedRowChanged?: (data: dump) => void }>(
  (props: DumpsGridProps, ref: React.ForwardedRef<IDumpsGridRef>) => {
    const token = useTokenRefresh()
    const currentScreenSize = useScreenSize()
    const navigate = useNavigate()
    const { client, service } = useQsAdminApiManager()
    const [userInfo, setUserInfo] = useState<AzureUserInfo | undefined>()

    const gridRef = useRef<DataGridRef>(null)

    const getGridHeight = useCallback(() => {
      let h = '79vh'
      if (currentScreenSize.isMedium) {
        h = '75vh'
      } else if (currentScreenSize.isSmall) {
        h = '120vh'
      } else if (currentScreenSize.isXSmall) {
        h = '75vh'
      }
      return h
    }, [currentScreenSize])

    const onExporting = (e: ExportingEvent) => {
      const workbook = new Workbook()
      const worksheet = workbook.addWorksheet('Dumps')
      const component: dxDataGrid = e.component
      exportDataGrid({ component, worksheet }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Dumps.xlsx')
        })
        component.clearSelection()
      })
    }

    const calculateTipologieValue = (rowData: dump) => {
      let tipologie: string = ''
      rowData.tipologie?.map((tipologia: tipologia_dump) => (tipologie = `${tipologie + tipologia.nome} `))
      return tipologie.trim()
    }

    const cellTipologieRender = (cellData: DataGridTypes.ColumnCellTemplateData) => {
      return (
        <>
          {cellData.data.tipologie?.map((tipologia: tipologia_dump, index: number) => {
            return (
              <div className={`dx-tag-content custom-tag-padding`} key={`ctc-${index}`}>
                <span>{tipologia.nome}</span>
              </div>
            )
          })}
        </>
      )
    }

    const calculateTipologieFilterExpression = (
      filterValue: any,
      selectedFilterOperation: string | null,
      target: string,
    ): string | (() => any) | Array<any> => {
      console.log('FLT', filterValue, selectedFilterOperation, target)

      if (target === 'filterBuilder' || target === 'filterRow') {
        if (selectedFilterOperation === '=' || selectedFilterOperation === '<>') {
          return [
            [new EdmLiteral(`tipologie/any(a: a/nome eq '${filterValue}')`), '=', selectedFilterOperation === '='],
          ]
        } else if (
          selectedFilterOperation === 'contains' ||
          selectedFilterOperation === 'startswith' ||
          selectedFilterOperation === 'endswith'
        ) {
          return [[new EdmLiteral(`tipologie/any(a: ${selectedFilterOperation}(a/nome, '${filterValue}'))`), '=', true]]
        } else {
          // caso notcontains
          return [[new EdmLiteral(`tipologie/any(a: contains(a/nome, '${filterValue}'))`), '=', false]]
        }
      } else if (target === 'headerFilter') {
        return [[new EdmLiteral(`tipologie/any(a: contains(a/nome, '${filterValue}'))`), '=', true]]
      }

      return filterValue
    }

    const downloadSelectedDumps = useCallback(async () => {
      const dumps = gridRef.current?.instance().getSelectedRowsData() as dump[] | undefined
      if (!dumps || dumps.length === 0) {
        notify('Selezionare almeno un dump.', 'warning', 3000)
        return
      }

      try {
        gridRef?.current?.instance().beginCustomLoading(`Download di ${dumps.length} dump .zip in corso....`)
        for (const dumpToDownload of dumps) {
          await downloadDump(client, dumpToDownload)
        }
      } catch (error: unknown) {
        notify(`Errore download dump. Dettagli : ${error}`, 'error', 2000)
      } finally {
        gridRef?.current?.instance().endCustomLoading()
      }
    }, [client])

    const dumpsDataSource = {
      store: new ODataStore({
        url: `${import.meta.env.VITE_QSADMINAPI_HOST}/dump`,
        key: 'id',
        keyType: 'Int32',
        version: 4,
        errorHandler: (e) => {
          console.error(e.errorDetails)
          if (!e.errorDetails) return
          notify(
            {
              message: `Errore : ${e.errorDetails?.message}`,
              type: 'error',
              displayTime: 5000,
            },
            {
              position: 'bottom center',
              direction: 'up-push',
            },
          )
        },
        beforeSend: ODataStoreRequestConfiguration(token),
        deserializeDates: false,
      }),
      expand: [
        'email',
        'licenza($expand=sede($expand=azienda($expand=agente($expand=commerciale_qs),rivenditore)))',
        'stato',
        'tipologie',
      ],
      filter: props.currentFilter?.length && props.currentFilter.length > 0 ? props.currentFilter : null,
      requireTotalCount: true,
    }

    useImperativeHandle(ref, () => {
      return {
        ref() {
          return gridRef
        },
      } satisfies IDumpsGridRef
    }, [])

    useEffect(() => {
      getAzureUserInformation().then((userInfo: AzureUserInfo | undefined) => {
        setUserInfo(userInfo)
      })
    }, [])

    return (
      <>
        <DataGrid
          id={'dumps-datagrid'}
          height={getGridHeight()}
          dataSource={dumpsDataSource}
          noDataText="Nessun dump trovato"
          className={'grid dx-card wide-card'}
          showBorders={false}
          showColumnLines={true}
          wordWrapEnabled={false}
          showRowLines={true}
          focusedRowEnabled={true}
          rowAlternationEnabled={true}
          allowColumnResizing={true}
          allowColumnReordering={true}
          width="100%"
          onFocusedRowChanged={(e: DataGridTypes.FocusedRowChangedEvent) => {
            if (e.row?.rowType === 'data' && props.onFocusedRowChanged) props.onFocusedRowChanged(e.row.data)
          }}
          onRowDblClick={(e: RowDblClickEvent<any, any>) => {
            if (e.rowType === 'data') navigate(`${e.data.id}`)
          }}
          onExporting={onExporting}
          onCellPrepared={onCellPrepared}
          ref={gridRef}
          columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
          remoteOperations={{
            filtering: true,
            grouping: true,
            groupPaging: true,
            paging: true,
            sorting: true,
            summary: false,
          }}
          grouping={{
            // Fondamentale per il raggruppamento veloce!!!
            autoExpandAll: true,
          }}
          groupPanel={{
            visible: currentScreenSize.isLarge || currentScreenSize.isMedium,
            emptyPanelText: 'Trascina qui una colonna per raggruppare',
          }}
        >
          <Pager
            visible={true}
            allowedPageSizes={'auto'}
            displayMode={'compact'}
            showPageSizeSelector={false}
            showInfo={true}
            showNavigationButtons={false}
            infoText={gridTotalItemsLabel}
          />
          <FilterRow visible={true} />
          <SearchPanel visible={true} width={currentScreenSize.isXSmall ? 200 : 250} />
          <Sorting mode="multiple"></Sorting>
          <FilterPanel visible={true} />
          <Export
            texts={{ exportAll: 'Esporta dati in excel', exportSelectedRows: 'Esporta dati selezionati in excel' }}
            enabled
            allowExportSelectedData
            formats={['xlsx']}
          />
          <Editing allowAdding={true} />
          <HeaderFilter visible={true} />
          <Selection
            allowSelectAll={true}
            mode="multiple"
            selectAllMode="allPages"
            showCheckBoxesMode="always"
            deferred={false}
          />
          <ColumnChooser enabled={true} />
          <Scrolling mode="virtual" />
          <StateStoring enabled={true} type="localStorage" storageKey={'dumps-datagrid'} savingTimeout={50} />
          <Column
            visible={true}
            type="buttons"
            width={currentScreenSize.isXSmall || currentScreenSize.isSmall ? '10%' : '2%'}
            alignment="left"
            showInColumnChooser={false}
          >
            <GridButton
              hint="Download zip"
              icon="download"
              onClick={async (e: ColumnButtonClickEvent) => {
                const dumpToDownload = e.row?.data as dump | undefined
                if (!dumpToDownload) return

                try {
                  gridRef?.current?.instance().beginCustomLoading('Download dump .zip in corso....')
                  await downloadDump(client, dumpToDownload)
                } catch (error: unknown) {
                  notify(`Errore download dump. Dettagli : ${error}`, 'error', 2000)
                } finally {
                  gridRef?.current?.instance().endCustomLoading()
                }
              }}
              visible={isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.TechDeveloper])}
            ></GridButton>
          </Column>
          <Column dataField={'id'} caption="ID" visible={false} allowSearch={false} dataType="number">
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'id', true)}>
              <Search enabled={true} searchExpr={'id'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'ded_Dis'}
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: dump = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`${data.id}`}
                  text={data.ded_Dis}
                  disabled={!isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.TechDeveloper])}
                ></ObjectCodeLink>
              )
            }}
            caption="DUMP ID"
            visible={false}
            allowSearch={false}
            dataType="string"
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'ded_Dis')}>
              <Search enabled={true} searchExpr={'ded_Dis'} />
            </HeaderFilter>
          </Column>
          {DateColumn({
            dataField: 'data_ricezione',
            caption: 'DATA RICEZIONE',
            width: 100,
            format: 'dd/MM/yyyy HH:mm',
            defaultSortOrder: 'desc',
          })}
          <Column
            dataField={'email.email'}
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
            caption="EMAIL"
            visible={true}
            allowSearch={false}
            dataType="string"
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'email.email')}>
              <Search enabled={true} searchExpr={'email.email'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'licenza.chiave'}
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: dump = cellData.row.data
              return (
                <ObjectCodeLink url={`/software/qs/${data.licenza?.id}`} text={data.licenza?.chiave}></ObjectCodeLink>
              )
            }}
            caption="LICENZA"
            visible={true}
            allowSearch={false}
            dataType="string"
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 130 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 130 : undefined}
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'licenza.chiave')}>
              <Search enabled={true} searchExpr={'licenza.chiave'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'licenza.sede.azienda.nome'}
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: dump = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`/clients/${data.licenza?.sede?.azienda?.id}`}
                  text={data.licenza?.sede?.azienda?.nome}
                ></ObjectCodeLink>
              )
            }}
            caption="AZIENDA"
            visible={true}
            allowSearch={false}
            dataType="string"
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 130 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 130 : undefined}
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'licenza.sede.azienda.nome')}>
              <Search enabled={true} searchExpr={'licenza.sede.azienda.nome'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'licenza.sede.nome'}
            caption="SEDE"
            visible={false}
            allowSearch={false}
            dataType="string"
            allowHeaderFiltering={false}
          />
          <Column
            dataField={'licenza.sede.azienda.agente.commerciale_qs.fullname'}
            caption={'AGENTE'}
            visible={true}
            allowSearch={false}
            dataType="string"
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'agente', 'commerciale_qs.fullname')}>
              <Search enabled={true} searchExpr={'commerciale_qs.fullname'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'licenza.sede.azienda.rivenditore.nome'}
            caption={'RIVENDITORE'}
            visible={false}
            allowSearch={false}
            dataType="string"
          >
            <HeaderFilter
              dataSource={columnSourceFactory(token, 'azienda', 'nome', undefined, [['is_rivenditore', true]])}
            >
              <Search enabled={true} searchExpr={'nome'} />
            </HeaderFilter>
          </Column>
          {DateColumn({
            dataField: 'data',
            caption: 'DATA',
            width: 100,
            format: 'dd/MM/yyyy HH:mm',
            defaultSortOrder: 'desc',
          })}
          <Column dataField={'stato.nome'} caption="STATO" visible={true} allowSearch={false} dataType="string">
            <HeaderFilter dataSource={columnSourceFactory(token, 'stato_dump', 'nome')}>
              <Search enabled={true} searchExpr={'nome'} />
            </HeaderFilter>
          </Column>
          <Column
            name={'info_tipologie'}
            caption={'TIPOLOGIE'}
            cellRender={cellTipologieRender}
            calculateCellValue={calculateTipologieValue}
            calculateFilterExpression={calculateTipologieFilterExpression}
            allowSearch={false}
            visible={true}
            dataType="string"
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'tipologia_dump', 'nome')}>
              <Search enabled={true} searchExpr={'nome'} />
            </HeaderFilter>
          </Column>
          <Column dataField={'piattaforma'} caption="PIATTAFORMA" visible={true} allowSearch={false} dataType="string">
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'piattaforma')}>
              <Search enabled={true} searchExpr={'piattaforma'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'piattaforma_versione'}
            caption="PIATTAFORMA VERSIONE"
            visible={true}
            allowSearch={false}
            dataType="string"
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'piattaforma_versione')}>
              <Search enabled={true} searchExpr={'piattaforma_versione'} />
            </HeaderFilter>
          </Column>
          <Column dataField={'app_version'} caption="APP VERSION" visible={true} allowSearch={false} dataType="string">
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'app_version')}>
              <Search enabled={true} searchExpr={'app_version'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'app_available_update'}
            caption="APP AVAILABLE UPDATE"
            visible={false}
            allowSearch={false}
            dataType="string"
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'app_available_update')}>
              <Search enabled={true} searchExpr={'app_available_update'} />
            </HeaderFilter>
          </Column>
          <Column dataField={'app_hotfix'} caption="APP HOTFIX" visible={true} allowSearch={false} dataType="string">
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'app_hotfix')}>
              <Search enabled={true} searchExpr={'app_hotfix'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'app_available_hotfix'}
            caption="APP AVAILABLE HOTFIX"
            visible={false}
            allowSearch={false}
            dataType="string"
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'app_available_hotfix')}>
              <Search enabled={true} searchExpr={'app_available_hotfix'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'common_version'}
            caption="COMMON VERSION"
            visible={true}
            allowSearch={false}
            dataType="string"
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'common_version')}>
              <Search enabled={true} searchExpr={'common_version'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'common_hotfix'}
            caption="COMMON HOTFIX"
            visible={true}
            allowSearch={false}
            dataType="string"
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'common_hotfix')}>
              <Search enabled={true} searchExpr={'common_hotfix'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'common_available_hotfix'}
            caption="COMMON AVAILABLE HOTFIX"
            visible={false}
            allowSearch={false}
            dataType="string"
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'common_available_hotfix')}>
              <Search enabled={true} searchExpr={'common_available_hotfix'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'service_pack_update'}
            caption="SERVICE PACK UPDATE"
            visible={true}
            allowSearch={false}
            dataType="string"
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'service_pack_update')}>
              <Search enabled={true} searchExpr={'service_pack_update'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'session_uptime'}
            caption="SESSION UPTIME"
            visible={true}
            allowSearch={false}
            dataType="number"
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'session_uptime', true)}>
              <Search enabled={true} searchExpr={'session_uptime'} />
            </HeaderFilter>
          </Column>
          <Column dataField={'gdi'} caption="GDI" visible={true} dataType="number" allowSearch={false}>
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'gdi', true)}>
              <Search enabled={true} searchExpr={'gdi'} />
            </HeaderFilter>
          </Column>
          <Column dataField={'gdi_peak'} caption="GDI PEAK" visible={true} dataType="number" allowSearch={false}>
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'gdi_peak', true)}>
              <Search enabled={true} searchExpr={'gdi_peak'} />
            </HeaderFilter>
          </Column>
          <Column dataField={'user_gdi'} caption="USER GDI" visible={true} dataType="number" allowSearch={false}>
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'user_gdi', true)}>
              <Search enabled={true} searchExpr={'user_gdi'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'user_gdi_peak'}
            caption="USER GDI PEAK"
            visible={true}
            dataType="number"
            allowSearch={false}
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'dump', 'user_gdi_peak', true)}>
              <Search enabled={true} searchExpr={'user_gdi_peak'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'stack_trace'}
            caption="STACK TRACE"
            visible={false}
            showInColumnChooser={false}
            dataType="string"
            allowHeaderFiltering={false}
          ></Column>
          <Column
            dataField={'cerdata'}
            caption="CERDATA"
            visible={false}
            showInColumnChooser={false}
            dataType="string"
            allowHeaderFiltering={false}
          ></Column>
          <Column
            dataField={'log'}
            caption="LOG"
            visible={false}
            showInColumnChooser={false}
            dataType="string"
            allowHeaderFiltering={false}
          ></Column>
          <Column
            dataField={'note'}
            caption="NOTE"
            visible={false}
            showInColumnChooser={false}
            dataType="string"
            allowHeaderFiltering={false}
          ></Column>
          <Toolbar>
            <Item
              location="before"
              locateInMenu="never"
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Refresh',
                text: 'Refresh',
                icon: 'refresh',
                onClick: (e: ClickEvent) => {
                  gridRef.current?.instance().refresh()
                },
                stylingMode: 'text',
              }}
            ></Item>
            <Item name="groupPanel" />
            <Item
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              visible={isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.TechDeveloper])}
              options={{
                onClick: downloadSelectedDumps,
                hint: 'Download dump selezionati',
                text: 'Download selezionati',
                icon: 'download',
                stylingMode: 'text',
              }}
            />
            <Item
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              visible={isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.TechDeveloper])}
              options={{
                onClick: async () => {
                  const dumps = gridRef.current?.instance().getSelectedRowsData()
                  if (dumps && dumps?.length > 0) {
                    const sedeDump: sede | null | undefined = dumps[0].licenza?.sede
                    const emailDump: email_aziendale | null | undefined = dumps[0].email
                    console.log(sedeDump, emailDump, dumps)
                    let dumpLink: string = `<a href="/dumps/${dumps[0].id}">${dumps[0].ded_Dis}</a> `
                    for (let index = 1; index < dumps.length; index++) {
                      const dump = dumps[index]
                      if (sedeDump?.id !== dump.licenza?.sede?.id) {
                        notify('Selezionare dump della stessa azienda.', 'warning', 3000)
                        return
                      }
                      if (emailDump?.id !== dump.email?.id) {
                        notify('Selezionare dump provenienti dallo stesso utente.', 'warning', 3000)
                        return
                      }
                      dumpLink += `<a href="/dumps/${dump.id}">${dump.ded_Dis}</a> `
                    }
                    gridRef?.current?.instance().beginCustomLoading('Creazione ticket in corso....')
                    try {
                      const stato = await service.stato_ticket(StatoTicket.APERTO).query()
                      const tipologia = await service.tipologia_ticket(TipologiaTicket.ASSISTENZA_TECNICA).query()
                      const ticket: ticket = {
                        id: 0,
                        ded_Dis: '',
                        ded_RootFam: '',
                        ded_Id: 0,
                        contattato: false,
                        attesa: false,
                        urgente: false,
                        notifica_chiusura: false,
                        rating: 0,
                        rating_note: null,
                        apertura: new Date().toISOString(),
                        chiusura: null,
                        descrizione: `Ticket creato da dump ${dumpLink}`,
                        soluzione: null,
                        leg_tag1: null,
                        leg_tag2: null,
                        durata: 0,
                        leg_contatto: emailDump?.email ?? '',
                        leg_n_chiamate: 0,
                        leg_email_contatto: emailDump?.email ?? '',
                        leg_phone_contatto: null,
                        assegnato: null,
                        stato: stato.data,
                        attachmentsFolderUrl: '',
                        sede: sedeDump,
                        tipologia_ticket: tipologia.data,
                        dumps: dumps,
                        customer_id: null,
                      }
                      const response = await createTicketWithApi(client, ticket)
                      gridRef?.current?.instance().clearSelection()
                      window.open(`/tickets/${response.data.ticketRecordId}`, '_blank')
                    } catch (error) {
                      notify(`Errore creazione ticket: ${error}`, 'error', 3000)
                    } finally {
                      gridRef?.current?.instance().endCustomLoading()
                    }
                  } else notify('Selezionare almeno un dump.', 'warning', 3000)
                },
                hint: 'Crea ticket',
                text: 'Crea ticket',
                icon: 'fas fa-ticket',
                stylingMode: 'text',
              }}
            />
            <Item
              name="columnChooserButton"
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              options={{ hint: 'Scelta colonne', text: 'Scelta colonne' }}
            />
            <Item
              name="exportButton"
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              options={{ hint: 'Esporta dati in excel' }}
            />
            <Item name="searchPanel" />
          </Toolbar>
        </DataGrid>
      </>
    )
  },
)

const DumpsGridMemoed = memo(DumpsGrid, (oldProps, newProps) => {
  console.log(oldProps, newProps)
  return oldProps.currentFilter === newProps.currentFilter
})

export default DumpsGridMemoed
